import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Brain, Zap, Users, Clock, CheckCircle, TrendingUp, Play, Pause, Download, Star, AlertCircle, BarChart3, Plus, X, Search, PhoneMissed, PhoneCall, Voicemail, Hash, Target, Award, Activity } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import { useChat } from '../../hooks/useChat';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';

// All constants outside component for stable references
const SCRIPT_TEMPLATES = {
  qualification: 'Hi [Name], this is [Your Name] from LeadGen Pro AI. I noticed your company [Company] has been growing rapidly. I wanted to quickly connect to see if our AI-powered lead generation platform could help you scale your sales process. Do you have 2 minutes?',
  followup: 'Hi [Name], I\'m following up on our previous conversation about [Topic]. I wanted to check if you had any questions about our proposal and if you\'re ready to move forward with the next steps.',
  demo: 'Hi [Name], I\'m calling to confirm our demo scheduled for [Time]. I\'m excited to show you how our AI dialer and lead scoring can increase your conversion rates by up to 40%.',
  closing: 'Hi [Name], I wanted to follow up on the proposal we sent over. Based on your needs, I believe our [Plan] plan would be the perfect fit. Are you ready to get started today?',
  voicemail: 'Hi [Name], this is [Your Name] from LeadGen Pro AI. I was calling to discuss how we can help [Company] generate more qualified leads using AI. I\'ll send you an email with more details. Feel free to call me back at [Phone]. Have a great day!',
};

const SAMPLE_CONTACTS = [
  { id: 1, name: 'Sarah Johnson', company: 'TechCorp Inc', phone: '+1 (555) 234-5678', score: 92, status: 'hot', title: 'VP of Marketing', notes: 'Interested in automation tools, budget approved Q1', email: 'sarah@techcorp.com', timezone: 'EST', attempts: 0 },
  { id: 2, name: 'Michael Chen', company: 'Growth Agency', phone: '+1 (555) 345-6789', score: 78, title: 'CEO', status: 'warm', notes: 'Needs follow-up on pricing, comparing with competitor', email: 'mchen@growth.io', timezone: 'PST', attempts: 1 },
  { id: 3, name: 'Emily Rodriguez', company: 'StartupXYZ', phone: '+2 (555) 456-7890', score: 65, title: 'Founder', status: 'warm', notes: 'Demo scheduled for next week, very interested', email: 'emily@startupxyz.com', timezone: 'CST', attempts: 0 },
  { id: 4, name: 'David Park', company: 'Enterprise Co', phone: '+1 (555) 567-8901', score: 88, title: 'Director of Sales', status: 'hot', notes: 'Ready to buy, needs contract review', email: 'dpark@enterprise.co', timezone: 'EST', attempts: 2 },
  { id: 5, name: 'Lisa Thompson', company: 'Media Group', phone: '+1 (555) 678-9012', score: 45, title: 'Marketing Manager', status: 'cold', notes: 'Initial outreach, found via LinkedIn', email: 'lisa@mediagroup.com', timezone: 'MST', attempts: 0 },
  { id: 6, name: 'James Wilson', company: 'Finance Corp', phone: '+1 (555) 789-0123', score: 71, title: 'CFO', status: 'warm', notes: 'Interested in ROI tracking features', email: 'jwilson@finance.co', timezone: 'EST', attempts: 1 },
];

const DISPOSITIONS = [
  { id: 'interested', label: 'Interested', color: 'bg-green-50 text-green-700 border-green-200', icon: Star },
  { id: 'callback', label: 'Call Back', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: PhoneCall },
  { id: 'not_interested', label: 'Not Interested', color: 'bg-red-50 text-red-700 border-red-200', icon: X },
  { id: 'no_answer', label: 'No Answer', color: 'bg-gray-50 text-gray-700 border-gray-200', icon: PhoneMissed },
  { id: 'voicemail', label: 'Left Voicemail', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Voicemail },
  { id: 'wrong_number', label: 'Wrong Number', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  { id: 'closed', label: 'Closed Won', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Award },
  { id: 'demo_scheduled', label: 'Demo Scheduled', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Target },
];

const DIALPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

const OUTCOME_COLORS = {
  interested: 'text-green-600 bg-green-50',
  callback: 'text-blue-600 bg-blue-50',
  closed: 'text-emerald-600 bg-emerald-50',
  completed: 'text-gray-600 bg-gray-50',
  no_answer: 'text-red-600 bg-red-50',
  voicemail: 'text-purple-600 bg-purple-50',
  not_interested: 'text-red-600 bg-red-50',
  demo_scheduled: 'text-indigo-600 bg-indigo-50',
  wrong_number: 'text-orange-600 bg-orange-50',
};

const TABS = [
  { id: 'dialer', label: 'Dialer', icon: Phone },
  { id: 'queue', label: 'Queue', icon: Users },
  { id: 'script', label: 'AI Script', icon: Brain },
  { id: 'coaching', label: 'AI Coaching', icon: Zap },
  { id: 'history', label: 'History', icon: Clock },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const INITIAL_HISTORY = [
  { id: 1, name: 'Alex Turner', company: 'SaaS Co', duration: '4:32', outcome: 'interested', time: '2 hours ago', score: 85, notes: 'Very interested in enterprise plan, send proposal', phone: '+2 (555) 111-2222' },
  { id: 2, name: 'Maria Garcia', company: 'Retail Plus', duration: '2:15', outcome: 'callback', time: '3 hours ago', score: 62, notes: 'Call back Thursday 2pm EST', phone: '+2 (555) 222-3333' },
  { id: 3, name: 'James Wilson', company: 'Finance Corp', duration: '6:48', outcome: 'closed', time: '5 hours ago', score: 95, notes: 'Closed! $24k deal. Contract sent.', phone: '+1 (555) 333-4444' },
  { id: 4, name: 'Tom Bradley', company: 'Tech Startup', duration: '0:45', outcome: 'no_answer', time: '6 hours ago', score: 55, notes: 'No answer, left voicemail', phone: '+1 (555) 444-5555' },
  { id: 5, name: 'Anna Kim', company: 'E-commerce Co', duration: '3:20', outcome: 'demo_scheduled', time: 'Yesterday', score: 78, notes: 'Demo scheduled for Friday 10am', phone: '+1 (555) 555-6666' },
];

const AIDialer = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('dialer');
  const [callState, setCallState] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [currentContact, setCurrentContact] = useState(null);
  const [aiCoaching, setAiCoaching] = useState('');
  const [aiCoachingLoading, setAiCoachingLoading] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [callQueue, setCallQueue] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [scriptText, setScriptText] = useState('');
  const [scriptLoading, setScriptLoading] = useState(false);
  const [selectedScript, setSelectedScript] = useState('qualification');
  const [powerDialerActive, setPowerDialerActive] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [showDispositionModal, setShowDispositionModal] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [dialpadInput, setDialpadInput] = useState('');
  const [showDialpad, setShowDialpad] = useState(false);
  const [voicemailDropped, setVoicemailDropped] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [coachingHistory, setCoachingHistory] = useState([]);
  const [generatedScript, setGeneratedScript] = useState('');
  const timerRef = useRef(null);
  // Use ref to track current contact in callbacks without stale closure
  const currentContactRef = useRef(null);
  // Stable ref for callDuration to avoid stale closures in handleSaveDisposition
  const callDurationRef = useRef(0);
  // Stable ref for callQueue to avoid stale closures in power dialer
  const callQueueRef = useRef([]);

  const { response: aiResponse, isLoading: aiLoading, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);
  const { response: scriptResponse, isLoading: scriptAiLoading, sendMessage: sendScriptMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    setCallQueue(SAMPLE_CONTACTS?.slice(0, 4));
    setCallHistory(INITIAL_HISTORY);
    setScriptText(SCRIPT_TEMPLATES?.qualification);
  }, []);

  // Keep callQueueRef in sync
  useEffect(() => {
    callQueueRef.current = callQueue;
  }, [callQueue]);

  useEffect(() => {
    if (callState === 'active') {
      timerRef.current = setInterval(() => {
        setCallDuration(d => {
          const next = d + 1;
          callDurationRef.current = next;
          return next;
        });
      }, 1000);
    } else {
      clearInterval(timerRef?.current);
      if (callState === 'idle') {
        setCallDuration(0);
        callDurationRef.current = 0;
      }
    }
    return () => clearInterval(timerRef?.current);
  }, [callState]);

  // Update ref whenever currentContact changes
  useEffect(() => {
    currentContactRef.current = currentContact;
  }, [currentContact]);

  useEffect(() => {
    if (aiResponse && !aiLoading) {
      setAiCoaching(aiResponse);
      setAiCoachingLoading(false);
      // Use ref to avoid stale closure on currentContact
      const contact = currentContactRef?.current;
      setCoachingHistory(prev => [
        { text: aiResponse, time: new Date()?.toLocaleTimeString(), contact: contact?.name || 'Unknown' },
        ...prev?.slice(0, 4)
      ]);
    }
  }, [aiResponse, aiLoading]);

  useEffect(() => {
    if (scriptResponse && !scriptAiLoading) {
      setGeneratedScript(scriptResponse);
      setScriptText(scriptResponse);
      setScriptLoading(false);
    }
  }, [scriptResponse, scriptAiLoading]);

  const showNotification = useCallback((msg, type = 'success') => {
    if (type === 'success') toast?.success(msg, 'AI Dialer');
    else if (type === 'error') toast?.error(msg, 'AI Dialer');
    else if (type === 'warning') toast?.warning(msg, 'AI Dialer');
    else toast?.info(msg, 'AI Dialer');
  }, [toast]);

  const formatDuration = useCallback((secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  }, []);

  // getAICoaching defined BEFORE handleStartCall so it can be referenced
  const getAICoaching = useCallback(async (contact) => {
    if (!contact) return;
    setAiCoachingLoading(true);
    sendMessage([
      { role: 'system', content: 'You are an expert sales coach providing real-time guidance during sales calls. Be concise and actionable. Format as 3 bullet points with emojis.' },
      { role: 'user', content: `I'm calling ${contact?.name || 'the prospect'}, ${contact?.title || 'a decision maker'} at ${contact?.company || 'their company'}. Lead score: ${contact?.score || 0}/100. Notes: ${contact?.notes || 'No notes'}. Give me 3 quick coaching tips for this call.` }
    ], { max_completion_tokens: 200 });
  }, [sendMessage]);

  const handleStartCall = useCallback((contact) => {
    if (!contact) return;
    setCurrentContact(contact);
    currentContactRef.current = contact;
    setCallState('calling');
    setTranscript([]);
    setAiCoaching('');
    setVoicemailDropped(false);
    setCallNotes('');
    setDisposition('');
    setTimeout(() => {
      setCallState('active');
      getAICoaching(contact);
    }, 1500);
  }, [getAICoaching]);

  const handleEndCall = useCallback(() => {
    setCallState('ended');
    setIsRecording(false);
    setShowDispositionModal(true);
  }, []);

  const handleSaveDisposition = useCallback(() => {
    // Use refs to get current values without stale closures
    const contact = currentContactRef?.current;
    const duration = callDurationRef?.current;
    const queue = callQueueRef?.current;
    setCallHistory(prev => [{
      id: Date.now(),
      name: contact?.name || 'Unknown',
      company: contact?.company || 'Unknown',
      duration: formatDuration(duration),
      outcome: selectedOutcome || 'completed',
      time: 'Just now',
      score: contact?.score || 0,
      notes: callNotes,
      phone: contact?.phone || '',
    }, ...prev]);
    setCallQueue(prev => prev?.filter(c => c?.id !== contact?.id));
    setShowDispositionModal(false);
    setCallState('idle');
    setCurrentContact(null);
    currentContactRef.current = null;
    setSelectedOutcome('');
    setCallNotes('');
    showNotification('Call saved successfully!');
    if (powerDialerActive && queue?.length > 1) {
      setTimeout(() => handleStartCall(queue?.[1]), 2000);
    }
  }, [selectedOutcome, callNotes, powerDialerActive, showNotification, formatDuration, handleStartCall]);

  const handleDropVoicemail = useCallback(() => {
    setVoicemailDropped(true);
    showNotification('Voicemail dropped successfully!');
    setTimeout(() => handleEndCall(), 1000);
  }, [showNotification, handleEndCall]);

  const handleDialpadKey = useCallback((key) => {
    setDialpadInput(prev => prev + key);
  }, []);

  const handleExportHistory = useCallback(() => {
    const csv = ['Name,Company,Phone,Duration,Outcome,Notes,Time',
      ...callHistory?.map(c =>
        `${c?.name},${c?.company},${c?.phone || ''},${c?.duration},${c?.outcome},"${c?.notes || ''}",${c?.time}`
      )
    ]?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'call_history.csv';
    a?.click();
    URL.revokeObjectURL(url);
    showNotification('Call history exported!');
  }, [callHistory, showNotification]);

  const generateScript = useCallback(() => {
    setScriptLoading(true);
    sendScriptMessage([
      { role: 'system', content: 'You are an expert sales script writer. Create compelling, conversational sales scripts that follow best practices.' },
      { role: 'user', content: `Generate a ${selectedScript} sales script for calling prospects. Make it natural, consultative, and focused on value. Include placeholders like [Name], [Company], [Your Name].` }
    ], { max_completion_tokens: 300 });
  }, [selectedScript, sendScriptMessage]);

  const filteredQueue = callQueue?.filter(c =>
    c?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const analyticsData = {
    totalCalls: callHistory?.length + 3,
    connected: Math.round((callHistory?.length + 3) * 0.72),
    avgDuration: '3:48',
    conversionRate: 34,
    outcomes: [
      { label: 'Interested', count: 4, color: 'bg-green-500' },
      { label: 'Demo Scheduled', count: 2, color: 'bg-indigo-500' },
      { label: 'Closed Won', count: 1, color: 'bg-emerald-500' },
      { label: 'Call Back', count: 3, color: 'bg-blue-500' },
      { label: 'No Answer', count: 2, color: 'bg-gray-400' },
      { label: 'Not Interested', count: 1, color: 'bg-red-400' },
    ],
    hourlyData: [2, 4, 6, 8, 5, 7, 9, 6, 4, 3, 5, 8],
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center space-x-2 ${
            notification?.type === 'success' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            <CheckCircle className="w-4 h-4" />
            <span>{notification?.msg}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">AI Dialer</h1>
              <p className="text-muted-foreground text-sm">AI-powered calling with real-time coaching, scripts & analytics</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => { setPowerDialerActive(!powerDialerActive); showNotification(powerDialerActive ? 'Power Dialer stopped' : 'Power Dialer activated!'); }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                powerDialerActive ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'
              }`}>
              {powerDialerActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{powerDialerActive ? 'Stop Power Dialer' : 'Power Dialer'}</span>
            </button>
            <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-full px-3 py-1.5">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-700 text-xs font-medium">AI Ready</span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Calls Today', value: callHistory?.length, icon: Phone, color: 'text-blue-500', bg: 'bg-blue-50' },
            { label: 'Avg Duration', value: analyticsData?.avgDuration, icon: Clock, color: 'text-purple-500', bg: 'bg-purple-50' },
            { label: 'Conversion Rate', value: `${analyticsData?.conversionRate}%`, icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50' },
            { label: 'Queue Size', value: callQueue?.length, icon: Users, color: 'text-orange-500', bg: 'bg-orange-50' },
          ]?.map((stat, i) => {
            const StatIcon = stat?.icon;
            return (
              <div key={i} className="bg-card border border-border rounded-xl p-4">
                <div className={`w-8 h-8 ${stat?.bg} rounded-lg flex items-center justify-center mb-2`}>
                  <StatIcon className={`w-4 h-4 ${stat?.color}`} />
                </div>
                <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                <p className="text-xs text-muted-foreground">{stat?.label}</p>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-muted/40 rounded-xl p-1 mb-6">
          {TABS?.map(tab => {
            const TabIcon = tab?.icon;
            const label = tab?.id === 'queue' ? `Queue (${callQueue?.length})` : tab?.label;
            return (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                  activeTab === tab?.id ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}>
                <TabIcon className="w-3.5 h-3.5" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {/* DIALER TAB */}
        {activeTab === 'dialer' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Call Panel */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Active Call</h3>
                <button onClick={() => setShowDialpad(!showDialpad)}
                  className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground">
                  <Hash className="w-3.5 h-3.5" />
                  <span>Dialpad</span>
                </button>
              </div>

              {showDialpad && (
                <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                  <input value={dialpadInput} readOnly
                    className="w-full text-center text-lg font-mono bg-transparent border-none outline-none text-foreground mb-2"
                    placeholder="Enter number" />
                  <div className="grid grid-cols-3 gap-2">
                    {DIALPAD_KEYS?.flat()?.map(key => (
                      <button key={key} onClick={() => handleDialpadKey(key)}
                        className="py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-medium text-foreground transition-colors">
                        {key}
                      </button>
                    ))}
                    <button onClick={() => setDialpadInput(prev => prev?.slice(0, -1))}
                      className="py-2 bg-red-50 hover:bg-red-100 rounded-lg text-sm text-red-500 transition-colors col-span-3">
                      ⌫ Delete
                    </button>
                  </div>
                </div>
              )}

              {callState === 'idle' && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground mb-2 text-sm">No active call</p>
                  <p className="text-xs text-muted-foreground mb-6">{callQueue?.length} contacts in queue</p>
                  <div className="space-y-2">
                    <button onClick={() => callQueue?.length > 0 && handleStartCall(callQueue?.[0])}
                      disabled={callQueue?.length === 0}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center justify-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <span>Start Next Call{callQueue?.[0] ? ` — ${callQueue?.[0]?.name}` : ''}</span>
                    </button>
                    {dialpadInput && (
                      <button onClick={() => handleStartCall({ id: 0, name: dialpadInput, company: 'Manual Dial', phone: dialpadInput, score: 50, status: 'warm', title: 'Unknown', notes: 'Manual dial' })}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center justify-center space-x-2">
                        <PhoneCall className="w-4 h-4" />
                        <span>Call {dialpadInput}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {(callState === 'calling' || callState === 'active') && currentContact && (
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-3 ${
                    callState === 'calling' ? 'bg-yellow-100 animate-pulse' : 'bg-green-100'
                  }`}>
                    <span className="text-3xl font-bold text-foreground">{currentContact?.name?.charAt(0) || '?'}</span>
                  </div>
                  <h3 className="font-semibold text-foreground">{currentContact?.name}</h3>
                  <p className="text-sm text-muted-foreground">{currentContact?.title} at {currentContact?.company}</p>
                  <p className="text-xs text-muted-foreground mb-3">{currentContact?.phone}</p>
                  {callState === 'calling' ? (
                    <p className="text-yellow-600 text-sm font-medium animate-pulse mb-4">📞 Calling...</p>
                  ) : (
                    <p className="text-green-600 text-sm font-medium mb-4">🟢 Connected — {formatDuration(callDuration)}</p>
                  )}
                  {callState === 'active' && (
                    <div className="flex justify-center space-x-3 mb-4">
                      <button onClick={() => setIsMuted(!isMuted)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isMuted ? 'bg-red-100 text-red-500' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}>
                        {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setIsSpeakerOn(!isSpeakerOn)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isSpeakerOn ? 'bg-blue-100 text-blue-500' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}>
                        {isSpeakerOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setIsRecording(!isRecording)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isRecording ? 'bg-red-100 text-red-500' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}>
                        <Activity className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex space-x-2">
                    {callState === 'active' && (
                      <button onClick={handleDropVoicemail} disabled={voicemailDropped}
                        className="flex-1 bg-muted hover:bg-muted/80 disabled:opacity-50 text-foreground px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        {voicemailDropped ? '✓ VM Dropped' : 'Drop Voicemail'}
                      </button>
                    )}
                    <button onClick={handleEndCall}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1">
                      <PhoneOff className="w-4 h-4" />
                      <span>End Call</span>
                    </button>
                  </div>
                </div>
              )}

              {callState === 'ended' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <PhoneOff className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-muted-foreground text-sm">Call ended — logging outcome...</p>
                </div>
              )}
            </div>

            {/* AI Coaching Panel */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Brain className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-foreground">AI Coaching</h3>
                {aiCoachingLoading && <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />}
              </div>
              {aiCoaching ? (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800 whitespace-pre-wrap">{aiCoaching}</p>
                </div>
              ) : (
                <div className="bg-muted/30 rounded-lg p-4 mb-4 text-center">
                  <Brain className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {callState === 'active' ? 'Generating coaching tips...' : 'Start a call to get AI coaching'}
                  </p>
                </div>
              )}

              {/* Live Transcript */}
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Live Transcript</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {transcript?.length > 0 ? transcript?.map((entry, i) => (
                    <div key={i} className={`text-xs p-2 rounded-lg ${
                      entry?.role === 'you' ? 'bg-blue-50 text-blue-800' :
                      entry?.role === 'them'? 'bg-green-50 text-green-800' : 'bg-muted/30 text-muted-foreground'
                    }`}>
                      <span className="font-medium capitalize">{entry?.role === 'you' ? 'You' : entry?.role === 'them' ? 'Prospect' : 'System'}:</span>{' '}
                      {entry?.text}
                    </div>
                  )) : (
                    <p className="text-xs text-muted-foreground text-center py-4">Transcript will appear here during the call</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QUEUE TAB */}
        {activeTab === 'queue' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Call Queue ({callQueue?.length})</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  placeholder="Search contacts..."
                  className="pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
            {filteredQueue?.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No contacts in queue</p>
                <p className="text-xs text-muted-foreground mt-1">Add contacts to start calling</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredQueue?.map((contact, i) => (
                  <div key={contact?.id} className="flex items-center justify-between p-4 bg-background border border-border rounded-xl hover:border-primary/30 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                        {contact?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{contact?.name}</p>
                        <p className="text-xs text-muted-foreground">{contact?.title} at {contact?.company}</p>
                        <p className="text-xs text-muted-foreground">{contact?.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        contact?.status === 'hot' ? 'bg-red-50 text-red-600' :
                        contact?.status === 'warm'? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                      }`}>
                        {contact?.status}
                      </div>
                      <div className="flex items-center space-x-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-lg">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        <span className="text-xs font-bold">{contact?.score}</span>
                      </div>
                      <button
                        onClick={() => handleStartCall(contact)}
                        disabled={callState !== 'idle'}
                        className="flex items-center space-x-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                      >
                        <Phone className="w-3 h-3" />
                        <span>Call</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SCRIPT TAB */}
        {activeTab === 'script' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">AI Script Generator</h3>
              <button
                onClick={generateScript}
                disabled={scriptLoading || scriptAiLoading}
                className="flex items-center space-x-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {(scriptLoading || scriptAiLoading) ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Generating...</span></>
                ) : (
                  <><Brain className="w-4 h-4" /><span>Generate Script</span></>
                )}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-4">
              {Object.keys(SCRIPT_TEMPLATES)?.map(type => (
                <button
                  key={type}
                  onClick={() => { setSelectedScript(type); setScriptText(SCRIPT_TEMPLATES?.[type]); }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium capitalize transition-colors ${
                    selectedScript === type ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            <textarea
              value={scriptText}
              onChange={e => setScriptText(e?.target?.value)}
              rows={10}
              className="w-full p-4 border border-border rounded-xl bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Select a script type or generate a custom script with AI..."
            />
            {generatedScript && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-xs text-purple-700 font-medium">✨ AI-generated script applied above</p>
              </div>
            )}
          </div>
        )}

        {/* COACHING TAB */}
        {activeTab === 'coaching' && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="w-5 h-5 text-yellow-500" />
                <h3 className="text-lg font-semibold text-foreground">AI Coaching History</h3>
              </div>
              {coachingHistory?.length > 0 ? (
                <div className="space-y-3">
                  {coachingHistory?.map((item, i) => (
                    <div key={i} className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-purple-700">{item?.contact}</span>
                        <span className="text-xs text-purple-500">{item?.time}</span>
                      </div>
                      <p className="text-sm text-purple-800 whitespace-pre-wrap">{item?.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No coaching history yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Start a call to receive AI coaching tips</p>
                </div>
              )}
            </div>
            {aiCoaching && (
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Brain className="w-5 h-5 text-purple-500" />
                  <h4 className="font-semibold text-foreground">Latest Coaching</h4>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-800 whitespace-pre-wrap">{aiCoaching}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Call History ({callHistory?.length})</h3>
              <button
                onClick={handleExportHistory}
                disabled={callHistory?.length === 0}
                className="flex items-center space-x-2 bg-muted hover:bg-muted/80 disabled:opacity-50 text-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
            {callHistory?.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No call history yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {callHistory?.map(call => {
                  const colorClass = OUTCOME_COLORS?.[call?.outcome] || 'text-gray-600 bg-gray-50';
                  return (
                    <div key={call?.id} className="flex items-center justify-between p-4 bg-background border border-border rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold text-sm text-foreground">
                          {call?.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">{call?.name}</p>
                          <p className="text-xs text-muted-foreground">{call?.company} • {call?.phone}</p>
                          {call?.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">{call?.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 text-right">
                        <div>
                          <p className="text-xs text-muted-foreground">{call?.duration}</p>
                          <p className="text-xs text-muted-foreground">{call?.time}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${colorClass}`}>
                          {call?.outcome?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Calls', value: analyticsData?.totalCalls, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Connected', value: analyticsData?.connected, color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Avg Duration', value: analyticsData?.avgDuration, color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Conversion Rate', value: `${analyticsData?.conversionRate}%`, color: 'text-orange-600', bg: 'bg-orange-50' },
              ]?.map((stat, i) => (
                <div key={i} className={`${stat?.bg} rounded-xl p-4`}>
                  <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  <p className="text-xs text-gray-600 mt-1">{stat?.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="font-semibold text-foreground mb-4">Call Outcomes</h4>
              <div className="space-y-3">
                {analyticsData?.outcomes?.map((outcome, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <span className="text-sm text-muted-foreground w-32">{outcome?.label}</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className={`${outcome?.color} h-2 rounded-full transition-all`}
                        style={{ width: `${(outcome?.count / analyticsData?.totalCalls) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground w-6">{outcome?.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="font-semibold text-foreground mb-4">Calls by Hour</h4>
              <div className="flex items-end space-x-1 h-24">
                {analyticsData?.hourlyData?.map((val, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-blue-500 rounded-t-sm transition-all"
                      style={{ height: `${(val / Math.max(...analyticsData?.hourlyData)) * 80}px` }}
                    />
                    <span className="text-xs text-muted-foreground mt-1">{8 + i}h</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Disposition Modal */}
        {showDispositionModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-semibold text-foreground mb-4">Log Call Outcome</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Call with <strong>{currentContactRef?.current?.name || 'contact'}</strong> ended. Duration: {formatDuration(callDurationRef?.current)}
              </p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {DISPOSITIONS?.map(d => {
                  const DIcon = d?.icon;
                  return (
                    <button
                      key={d?.id}
                      onClick={() => setSelectedOutcome(d?.id)}
                      className={`flex items-center space-x-2 p-3 rounded-xl border text-sm font-medium transition-colors ${
                        selectedOutcome === d?.id ? d?.color + ' ring-2 ring-offset-1 ring-current' : 'bg-background border-border hover:bg-muted/50'
                      }`}
                    >
                      <DIcon className="w-4 h-4" />
                      <span>{d?.label}</span>
                    </button>
                  );
                })}
              </div>
              <textarea
                value={callNotes}
                onChange={e => setCallNotes(e?.target?.value)}
                placeholder="Add call notes..."
                rows={3}
                className="w-full p-3 border border-border rounded-xl bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 mb-4"
              />
              <div className="flex space-x-3">
                <button
                  onClick={() => { setShowDispositionModal(false); setCallState('idle'); setCurrentContact(null); currentContactRef.current = null; }}
                  className="flex-1 px-4 py-2 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleSaveDisposition}
                  disabled={!selectedOutcome}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AIDialer;
